import classNames from 'classnames';
import {
  useUpdateCampaign,
  CAMPAIGN_STATUS_BADGE_PROPS,
  PendingChangesIcon,
  type ManagerGetCampaignModel,
  useGetManagerCampaignPausedReasons,
} from 'entities/campaign';
import { Badge } from 'shared/ui/Badge';
import { MutatableSwitcher, getRunCampaignError } from 'shared/ui/Switcher';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { FormSelect } from 'shared/ui/Select';

import { getCampaignPausedReasonsOptions } from './get-paused-reasons-options';
import { ManagerCampaignDetailsAlertBoxes } from './ManagerCampaignDetailsAlertBoxes';
import { usePendingRunStatus } from './usePendingRunStatus';

interface ManagerCampaignDetailsProps {
  campaign: ManagerGetCampaignModel;
  afterSwitchOff?: () => void;
  skipCampaignRefetch?: boolean;
  skipCampaignUpdate?: boolean;
  afterCampaignReactivate?: () => void;
}

export function ManagerCampaignDetails({
  campaign,
  afterSwitchOff,
  skipCampaignRefetch,
  skipCampaignUpdate,
  afterCampaignReactivate,
}: ManagerCampaignDetailsProps) {
  const status = campaign.status;
  const isDeactivated = status === 'deactivated';
  const useUpdateFn = () => useUpdateCampaign(campaign.id, skipCampaignUpdate, skipCampaignRefetch);
  const campaignPausedReasonsQuery = useGetManagerCampaignPausedReasons(campaign.id);
  const campaignPausedReasonsData = campaignPausedReasonsQuery.data?.data;
  const campaignPausedReasonsOptions = getCampaignPausedReasonsOptions(campaignPausedReasonsData);

  const showNoCCBadge =
    !campaign.hasCreditCard &&
    campaign.contractor.paymentMethod === 'stripe' &&
    status !== 'noCcAdded' &&
    campaign.type !== 'sales';
  const showPausedReasonsSelect = !campaign.isActive && campaignPausedReasonsOptions;
  const campaignPausedReasonId = campaign.pausedReasonId?.toString() || '';
  const { isPendingIcon, tooltipText } = usePendingRunStatus(campaign);

  return (
    <div className="border-b border-slate-100 pb-8">
      <div className="mb-4 flex items-center justify-between">
        <div
          className="text-sm"
          {...getDataAutoTestAttributes([`campaign-name`]).attributes}
        >
          {campaign.name}
        </div>
        <div className="flex flex-col items-end">
          <Badge {...CAMPAIGN_STATUS_BADGE_PROPS[status]} />
          {showNoCCBadge && (
            <Badge
              {...CAMPAIGN_STATUS_BADGE_PROPS.noCcAdded}
              className="mt-1"
            />
          )}
        </div>
      </div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex justify-between">
          <div className={classNames('text-sm', isDeactivated && 'text-slate-400')}>Run campaign</div>
          {isPendingIcon && (
            <PendingChangesIcon
              className="ml-2 mt-[-2px]"
              blockingMessage={tooltipText}
              appliesAt={campaign.pendingChanges?.appliesAt ?? null}
            />
          )}
        </div>
        <MutatableSwitcher
          isDisabled={isDeactivated}
          useUpdate={useUpdateFn}
          afterMutate={(isChecked) => {
            if (afterSwitchOff && !isChecked) {
              afterSwitchOff();
            }
          }}
          buildMutateArgs={(isActive) => ({ payload: { isActive } })}
          getErrorMessage={getRunCampaignError}
          name="isActive"
          isChecked={campaign.isActive}
          {...getDataAutoTestAttributes(['switcher-run-campaign'])}
        />
      </div>
      <ManagerCampaignDetailsAlertBoxes
        campaign={campaign}
        afterCampaignReactivate={afterCampaignReactivate}
      />
      {showPausedReasonsSelect && (
        <FormSelect
          title="Pausing reason"
          name="pausedReason"
          wrapperClass="mt-4"
          options={campaignPausedReasonsOptions}
          isCancel
          selectedOptionId={campaignPausedReasonId}
          {...getDataAutoTestAttributes(['select-campaign-pausing-reason'])}
        />
      )}
      {campaign.managerName && (
        <div className="mt-4 flex justify-between text-sm">
          <span className="mr-1">Partnerships manager: </span>
          <span {...getDataAutoTestAttributes([`campaign-partnerships-manager`]).attributes}>
            {campaign.managerName}
          </span>
        </div>
      )}
    </div>
  );
}
